import { FC, Fragment, ReactNode, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconButton, Divider, Menu, Typography, MenuItem } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  deleteFilterByRoleID,
  getFilterRoleByID,
} from "store/filterRole/thunks";
import variables from "../../styles/partials/_variables.module.scss";
import Modal from "components/Modal/Modal";
import { selectFilterRole } from "store/filterRole/selectors";
import { useAppSelector } from "utils/hooks/storeHooks";

type Props = {
  crudId: number; // The id that will be used in the delete, edit and other CRUD services
  memberCount?: number;
};

type Options = {
  name: string;
  desc: string;
  icon: ReactNode;
};

const MoreOptionsBtn: FC<Props> = ({ crudId, memberCount }: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentRole = useAppSelector(selectFilterRole);
  const [openModal, setOpenModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null); // sets the button element that is clicked
  const openMenu = Boolean(anchorEl);
  const options: Options[] = [
    {
      name: "members",
      desc: "View members",
      icon: <VisibilityIcon color="primary" />,
    },
    {
      name: "details",
      desc: "Edit role",
      icon: <EditIcon color="primary" />,
    },
    {
      name: "delete",
      desc: "Delete role",
      icon: <DeleteIcon color="primary" />,
    },
  ];

  // Handlers for opening (in the correct position) and closing the menu
  const handleOpenMoreOptions = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    dispatch(getFilterRoleByID(crudId));
  };

  const handleCloseMoreOptions = () => {
    setAnchorEl(null);
  };

  const handleOptions = async (label: string) => {
    switch (label) {
      case "delete":
        setOpenModal(true);
        break;
      default:
        navigate(`/RolesManagement/${crudId}/#${label}`);
        handleCloseMoreOptions();
        break;
    }
  };

  // Handlers for interacting with the delete modal
  const handleCloseModal = () => {
    setOpenModal(false);
    handleCloseMoreOptions();
  };

  const handleDeleteRole = () => {
    dispatch(deleteFilterByRoleID(crudId));
    handleCloseModal();
  };

  return (
    <Fragment>
      <IconButton
        disableRipple
        sx={{ padding: 0 }}
        onClick={handleOpenMoreOptions}
      >
        <MoreHorizIcon
          color="primary"
          sx={{
            backgroundColor: variables.backgroundGray,
            width: "24px",
            borderRadius: "4px",
          }}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        elevation={3}
        MenuListProps={{ sx: { padding: 0 } }}
        open={openMenu}
        slotProps={{ paper: { sx: { borderRadius: "8px" } } }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={handleCloseMoreOptions}
      >
        <Fragment>
          {options.map((option: Options) => {
            return (
              <Fragment>
                <MenuItem
                  disableTouchRipple
                  onClick={() => handleOptions(option.name)}
                >
                  {option.icon}
                  <Typography ml="0.5rem">{option.desc}</Typography>
                </MenuItem>
                <Divider
                  sx={{ "&.MuiDivider-middle": { margin: "0 1rem" } }}
                  variant="middle"
                />
              </Fragment>
            );
          })}
        </Fragment>
        <Modal
          actionLabel="Yes, delete"
          cancelActionLabel="Cancel"
          isActionBtnDisabled={!!memberCount}
          open={openModal}
          padding="3.5rem"
          title="Delete Role"
          titleHelperText={
            memberCount && memberCount > 0
              ? `${currentRole.name} can't be deleted because ${memberCount} members are still asigned to this role. Change the users' roles first.`
              : `Are you sure you want to delete the "${currentRole.name}" role?`
          }
          width="700px"
          onClose={handleCloseModal}
          onSubmit={handleDeleteRole}
        />
      </Menu>
    </Fragment>
  );
};

export default MoreOptionsBtn;
